import { useEffect, useState } from "react";
import { api, formatApiError } from "../../api/client";
import { useAuth } from "../../store/auth";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";

const COUNTRIES = [
  { value: "ES", label: "España" },
  { value: "MX", label: "México" },
  { value: "CO", label: "Colombia" },
  { value: "CL", label: "Chile" },
  { value: "AR", label: "Argentina" },
  { value: "PE", label: "Perú" },
  { value: "UY", label: "Uruguay" },
  { value: "OTROS", label: "Otros" },
];

export default function SettingsOrganization() {
  const { user, organization, setOrganization } = useAuth();
  const canEdit = user?.role === "owner" || user?.role === "admin";

  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (organization) {
      setForm({
        name: organization.name || "",
        country: organization.country || "ES",
        sector: organization.sector || "",
        size: organization.size?.toString() || "1",
      });
    }
  }, [organization]);

  if (!form) return null;

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      const { data } = await api.put("/organization", {
        name: form.name,
        country: form.country,
        sector: form.sector,
        size: parseInt(form.size, 10) || 1,
      });
      setOrganization(data);
      toast.success("Datos de la organización actualizados.");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "No se pudo guardar.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="settings-org-page" className="max-w-2xl">
      <h1 className="text-[20px] font-semibold tracking-tight text-slate-900">Organización</h1>
      <p className="text-[14px] text-slate-500 mt-1">
        Datos generales de tu organización.
      </p>

      <form onSubmit={submit} className="mt-6 space-y-4 bg-white border border-slate-200 rounded-md p-6">
        <div>
          <Label>Nombre</Label>
          <Input
            data-testid="org-name"
            value={form.name}
            disabled={!canEdit}
            onChange={(e) => update("name", e.target.value)}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>País</Label>
            <Select value={form.country} onValueChange={(v) => update("country", v)} disabled={!canEdit}>
              <SelectTrigger data-testid="org-country"><SelectValue /></SelectTrigger>
              <SelectContent>
                {COUNTRIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Tamaño (empleados)</Label>
            <Input
              type="number"
              min="1"
              data-testid="org-size"
              value={form.size}
              disabled={!canEdit}
              onChange={(e) => update("size", e.target.value)}
            />
          </div>
        </div>

        <div>
          <Label>Sector</Label>
          <Input
            data-testid="org-sector"
            value={form.sector}
            disabled={!canEdit}
            onChange={(e) => update("sector", e.target.value)}
          />
        </div>

        {error && <div data-testid="org-error" className="text-[12px] text-destructive">{error}</div>}

        {canEdit ? (
          <div className="pt-2">
            <Button type="submit" disabled={saving} data-testid="org-save">
              {saving ? "Guardando..." : "Guardar cambios"}
            </Button>
          </div>
        ) : (
          <div className="text-[12px] text-slate-500">
            Sólo los propietarios y administradores pueden editar estos datos.
          </div>
        )}
      </form>
    </div>
  );
}
